import {
  WSBalanceChangedEvent,
  WSBlacklistUpdateEvent,
  WSCashbackRewardUpdatedEvent,
  WSCsCompensationUpdatedEvent,
  WSDepositSuccessEvent,
  WSMegaShareUpdatedEvent,
  WSPlaySpinUpdatedEvent,
  WSReferralCommissionRewardUpdatedEvent,
  WSReferralTokenSettingsChangeEvent,
  WSSecretCodeUpdatedEvent,
  WSSkinnerBoxAmountChangedEvent,
  WSTokenChangeEvent,
  WSWithdrawalCancelledEvent,
  WS_EVENT,
} from "@constants/ws";
import { RootState } from "@stores";
import { isTokenExpired } from "@utils/jwt";
import logger from "@utils/logger";
import { getWsUrl } from "@utils/websocket";
import { AuthToken, WSEventResponse, WSEventType } from "kz-ui-sdk";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDocumentVisibilityChange } from "./useDocumentVisibilityChange";
import { useEventListener } from "./useEventListener";
import useNetworkStatus from "./useNetworkStatus";
import { useWebSocketManual } from "./useWS";

export function useAppWsEvent() {
  const isOnline = useNetworkStatus();

  const isVisible = useDocumentVisibilityChange({
    // 10 minutes delay
    delay: 600000,
    delayType: "invisible",
  });

  const auth = useSelector<RootState, AuthToken | null | undefined>((state) => state?.auth?.oauth);
  const isRefreshingToken = useSelector<RootState, boolean | undefined>((state) => state?.auth?.isRefreshingToken);

  const token: string | null = useMemo(() => {
    if (auth?.access_token) {
      // Check if token is expired
      if (isTokenExpired(auth.access_token)) {
        logger._console.log("WS token expired");
        return null;
      }
      return auth.access_token;
    }
    return null;
  }, [auth]);

  const isReady = token && !isRefreshingToken && isOnline && isVisible;

  // To prevent multiple connections, it's best not to use the useAppWsEvent hook in multiple components.
  const { lastJsonMessage } = useWebSocketManual<WSEventResponse>({
    url: isReady ? getWsUrl(token) : undefined,
    maxRetries: Infinity,
    heartbeat: true,
  });

  useEffect(() => {
    if (lastJsonMessage) {
      try {
        if (lastJsonMessage.type === "event") {
          switch (lastJsonMessage.payload.event) {
            case WSEventType.DEPOSIT_SUCCESS: {
              const event: WSDepositSuccessEvent = new CustomEvent(WS_EVENT.DEPOSIT_SUCCESS, {
                detail: {
                  fundingTx: lastJsonMessage.payload.fundingTx,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.BALANCE_CHANGED: {
              const event: WSBalanceChangedEvent = new CustomEvent(WS_EVENT.BALANCE_CHANGED, {
                detail: {
                  balance: lastJsonMessage.payload.balance,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.TOKEN_CHANGE_EVENT: {
              const event: WSTokenChangeEvent = new CustomEvent(WS_EVENT.TOKEN_CHANGE_EVENT, {
                detail: {
                  tokenCount: lastJsonMessage.payload.tokenCount,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.BLACKLIST_UPDATE: {
              const event: WSBlacklistUpdateEvent = new CustomEvent(WS_EVENT.BLACKLIST_UPDATE, {
                detail: {
                  isBlacklisted: lastJsonMessage.payload.isBlacklisted,
                  timestamp: lastJsonMessage.payload.timestamp,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.REFERRAL_TOKEN_SETTINGS_CHANGE: {
              const event: WSReferralTokenSettingsChangeEvent = new CustomEvent(
                WS_EVENT.REFERRAL_TOKEN_SETTINGS_CHANGE,
                {
                  detail: {},
                },
              );
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.SKINNER_BOX_TX_UPDATED: {
              const event: WSSkinnerBoxAmountChangedEvent = new CustomEvent(WS_EVENT.SKINNER_BOX_AMOUNT_CHANGED, {
                detail: {
                  availableAmount: lastJsonMessage.payload.availableAmount,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.COMPENSATION_UPDATED: {
              const event: WSCsCompensationUpdatedEvent = new CustomEvent(WS_EVENT.CS_COMPENSATION_UPDATED, {
                detail: {
                  compensationTxId: lastJsonMessage?.payload?.compensationTxId,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.PLAY_SPIN_UPDATED: {
              const event: WSPlaySpinUpdatedEvent = new CustomEvent(WS_EVENT.PLAY_SPIN_UPDATED, {
                detail: {
                  lastPlaySpinTx: lastJsonMessage.payload.lastPlaySpinTx,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.MEGA_SHARE_UPDATED: {
              const event: WSMegaShareUpdatedEvent = new CustomEvent(WS_EVENT.MEGA_SHARE_UPDATED, {
                detail: {
                  megaShareTx: lastJsonMessage.payload.megaShareTx,
                  sharedMegaShareTx: lastJsonMessage.payload.sharedMegaShareTx,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.CASHBACK_UPDATED: {
              const event: WSCashbackRewardUpdatedEvent = new CustomEvent(WS_EVENT.CASHBACK_REWARD_UPDATED, {
                detail: {
                  instant: lastJsonMessage.payload.instant,
                  daily: lastJsonMessage.payload.daily,
                  weekly: lastJsonMessage.payload.weekly,
                  monthly: lastJsonMessage.payload.monthly,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.REFERRAL_COMMISSION_UPDATED: {
              const event: WSReferralCommissionRewardUpdatedEvent = new CustomEvent(
                WS_EVENT.REFERRAL_COMMISSION_REWARD_UPDATED,
                {
                  detail: {},
                },
              );
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.WITHDRAWAL_CANCELLED: {
              const event: WSWithdrawalCancelledEvent = new CustomEvent(WS_EVENT.WITHDRAWAL_CANCELLED, {
                detail: {
                  fundingTx: lastJsonMessage.payload.fundingTx,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            case WSEventType.SECRET_CODE_UPDATED: {
              const event: WSSecretCodeUpdatedEvent = new CustomEvent(WS_EVENT.SECRET_CODE_UPDATED, {
                detail: {
                  campaign: lastJsonMessage.payload.campaign,
                  participant: lastJsonMessage.payload.participant,
                },
              });
              window.dispatchEvent(event);
              break;
            }

            default:
              break;
          }
        }
      } catch (error) {
        logger._console.error(error);
      }
    }
  }, [lastJsonMessage]);
}

export function useWSDepositSuccess(options: { cb?: (event: WSDepositSuccessEvent) => void }) {
  const { cb } = options;
  useEventListener<WSDepositSuccessEvent>({
    eventName: WS_EVENT.DEPOSIT_SUCCESS,
    handler: cb,
  });
}

export function useWSBalanceChanged(options: { cb?: (event: WSBalanceChangedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSBalanceChangedEvent>({
    eventName: WS_EVENT.BALANCE_CHANGED,
    handler: cb,
  });
}

export function useWSTokenChange(options: { cb?: (event: WSTokenChangeEvent) => void }) {
  const { cb } = options;
  useEventListener<WSTokenChangeEvent>({
    eventName: WS_EVENT.TOKEN_CHANGE_EVENT,
    handler: cb,
  });
}

export function useWSBlacklistUpdate(options: { cb?: (event: WSBlacklistUpdateEvent) => void }) {
  const { cb } = options;
  useEventListener<WSBlacklistUpdateEvent>({
    eventName: WS_EVENT.BLACKLIST_UPDATE,
    handler: cb,
  });
}

export function useWSReferralTokenSettingsChange(options: {
  cb?: (event: WSReferralTokenSettingsChangeEvent) => void;
}) {
  const { cb } = options;
  useEventListener<WSReferralTokenSettingsChangeEvent>({
    eventName: WS_EVENT.REFERRAL_TOKEN_SETTINGS_CHANGE,
    handler: cb,
  });
}

export function useWSSkinnerBoxAmountChanged(options: { cb?: (event: WSSkinnerBoxAmountChangedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSSkinnerBoxAmountChangedEvent>({
    eventName: WS_EVENT.SKINNER_BOX_AMOUNT_CHANGED,
    handler: cb,
  });
}

export function useWSCsCompensationUpdated(options: { cb?: (event: WSCsCompensationUpdatedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSCsCompensationUpdatedEvent>({
    eventName: WS_EVENT.CS_COMPENSATION_UPDATED,
    handler: cb,
  });
}

export function useWSPlaySpinUpdated(options: { cb?: (event: WSPlaySpinUpdatedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSPlaySpinUpdatedEvent>({
    eventName: WS_EVENT.PLAY_SPIN_UPDATED,
    handler: cb,
  });
}

export function useWSMegaShareUpdated(options: { cb?: (event: WSMegaShareUpdatedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSMegaShareUpdatedEvent>({
    eventName: WS_EVENT.MEGA_SHARE_UPDATED,
    handler: cb,
  });
}

export function useWSWithdrawalCancelled(options: { cb?: (event: WSWithdrawalCancelledEvent) => void }) {
  const { cb } = options;
  useEventListener<WSWithdrawalCancelledEvent>({
    eventName: WS_EVENT.WITHDRAWAL_CANCELLED,
    handler: cb,
  });
}

export function useWSCashbackRewardUpdated(options: { cb?: (event: WSCashbackRewardUpdatedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSCashbackRewardUpdatedEvent>({
    eventName: WS_EVENT.CASHBACK_REWARD_UPDATED,
    handler: cb,
  });
}

export function useWSReferralCommissionRewardUpdated(options: {
  cb?: (event: WSReferralCommissionRewardUpdatedEvent) => void;
}) {
  const { cb } = options;
  useEventListener<WSReferralCommissionRewardUpdatedEvent>({
    eventName: WS_EVENT.REFERRAL_COMMISSION_REWARD_UPDATED,
    handler: cb,
  });
}

export function useWSSecretCodeUpdated(options: { cb?: (event: WSSecretCodeUpdatedEvent) => void }) {
  const { cb } = options;
  useEventListener<WSSecretCodeUpdatedEvent>({
    eventName: WS_EVENT.SECRET_CODE_UPDATED,
    handler: cb,
  });
}
